import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Input, Stepper, useDesignSystemTheme } from "@xinghai/ui";
import { ApiError, authApi } from "../../lib/api";
import { fieldErrors, friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { COPYRIGHT_LINE } from "../../lib/constants";
import { useTheme } from "../../app/theme";
import { useAuth } from "./useAuth";
import { onboardingIndex, onboardingSteps } from "../student/onboarding";
import { EMPTY_REGISTER_FORM, toRegisterPayload, validateAccountStep } from "./registerValidation";
import type { RegisterErrors, RegisterForm } from "./registerValidation";

/** docs/ROADMAP.md A1 — 班级邀请码自助注册. */
export function RegisterPage() {
  const { me, isLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const [params] = useSearchParams();
  const [form, setForm] = useState<RegisterForm>({
    ...EMPTY_REGISTER_FORM,
    invite_code: params.get("code") ?? "",
  });
  const [errors, setErrors] = useState<RegisterErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * Optional nicety: when the server offers `auth/invite-class`, show the
   * class name as soon as the code looks complete. Any failure is silent —
   * the code is validated for real on submit.
   */
  const code = form.invite_code.trim();
  const classPreview = useQuery({
    queryKey: qk.invitePreview(`class:${code}`),
    queryFn: async () => {
      try {
        const res = await authApi.inviteClass(code);
        return res.valid && res.class_name ? res : null;
      } catch {
        return null;
      }
    },
    enabled: code.length >= 4,
    retry: false,
    staleTime: 60_000,
  });

  const register = useMutation({
    mutationFn: () => authApi.register(toRegisterPayload(form)),
    onSuccess: (data) => {
      qc.setQueryData(qk.me, data);
      void navigate(data.consent_required ? "/consent" : "/s", { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "INVALID_INVITE_CODE") {
        setErrors({ invite_code: "这个邀请码用不了，请找老师确认。" });
        return;
      }
      if (err instanceof ApiError && err.code === "USERNAME_TAKEN") {
        setErrors({ username: "这个用户名已经有人用了，换一个吧。" });
        return;
      }
      // 422 from the shared zod schema — put each issue on its own field.
      const fields = fieldErrors(err);
      const keys = Object.keys(fields);
      if (keys.length > 0) {
        setErrors(fields as RegisterErrors);
        setServerError(null);
        return;
      }
      setServerError(friendlyMessage(err, "注册没有成功，请检查网络后重试。"));
    },
  });

  if (!isLoading && me) return <Navigate to="/" replace />;

  const set = <K extends keyof RegisterForm>(key: K, value: RegisterForm[K]) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const onSubmit = () => {
    setServerError(null);
    const e = validateAccountStep(form);
    if (!form.consent) e.consent = "请勾选同意三项授权后再注册。";
    setErrors(e);
    if (Object.keys(e).length === 0) register.mutate();
  };

  return (
    <main className="xz-login">
      <form
        className="xz-login__card xz-register gz-panel gz-panel--raised"
        onSubmit={(ev) => {
          ev.preventDefault();
          onSubmit();
        }}
      >
        <Stepper
          steps={onboardingSteps("register")}
          current={onboardingIndex("register")}
          label="开始前的四步"
        />
        <h1 className="gz-type-heading">注册账号</h1>
        <p className="gz-muted">用老师发的班级邀请码开通账号。方言和出生年份可以之后再补充。</p>
            <Field
              label="班级邀请码"
              error={errors.invite_code}
              hint={classPreview.data ? undefined : "在课程群或课堂上发给你"}
            >
              <Input
                value={form.invite_code}
                autoCapitalize="characters"
                autoCorrect="off"
                onChange={(e) => set("invite_code", e.target.value)}
              />
            </Field>
            {classPreview.data && (
              <p className="gz-notice" role="status">
                <Icon id="ui-check-circle" size={20} />
                这个邀请码对应：{classPreview.data.class_name}
              </p>
            )}
            <Field
              label="用户名"
              error={errors.username}
              hint="3–32 位小写字母、数字或下划线，登录时用"
            >
              <Input
                value={form.username}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="username"
                onChange={(e) => set("username", e.target.value.toLowerCase())}
              />
            </Field>
            <Field label="姓名" error={errors.display_name} hint="用真实姓名，方便老师认人">
              <Input
                value={form.display_name}
                onChange={(e) => set("display_name", e.target.value)}
              />
            </Field>
            <Field label="密码" error={errors.password} hint="至少 10 位">
              <Input
                type="password"
                value={form.password}
                autoComplete="new-password"
                onChange={(e) => set("password", e.target.value)}
              />
            </Field>
            <Field label="再输一次密码" error={errors.password2}>
              <Input
                type="password"
                value={form.password2}
                autoComplete="new-password"
                onChange={(e) => set("password2", e.target.value)}
              />
            </Field>
            <label className="xz-check">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
              />
              <span>我同意将录音用于训练、展示和商业使用（未满 18 岁还需要监护人确认）。</span>
            </label>
            {errors.consent && (
              <p className="gz-notice gz-notice--error" role="alert">
                {errors.consent}
              </p>
            )}
            {serverError && (
              <p className="gz-notice gz-notice--error" role="alert">
                <Icon id="ui-error" size={20} />
                {serverError}
              </p>
            )}
            <Button type="submit" variant="primary" size="lg" block loading={register.isPending}>
              完成注册
            </Button>
            <p className="gz-caption">
              已经有账号了？<Link to="/login">直接登录</Link>
            </p>
            <p className="gz-caption xz-login__org">{COPYRIGHT_LINE}</p>
      </form>
    </main>
  );
}
